import { Fragment } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Check, Minus, ShieldCheck } from 'lucide-react';

// Papéis na ordem hierárquica (Master → Agente)
const PAPEIS = [
  { code: 'ADMIN', label: 'Administrador', desc: 'Master — acesso total' },
  { code: 'TESOUREIRO', label: 'Tesouraria', desc: 'Pagamentos e liberação financeira' },
  { code: 'COORDENADOR', label: 'Coordenação', desc: 'Avaliza renovações' },
  { code: 'ANALISTA', label: 'Analista', desc: 'Aprova operações novas' },
  { code: 'AGENTE', label: 'Agente', desc: 'Cadastra associados e operações' },
];

// Ações × papéis que podem (fiel aos requireRole dos endpoints)
const ACOES: { area: string; acao: string; papeis: string[] }[] = [
  { area: 'Associados', acao: 'Cadastrar / editar associado', papeis: ['ADMIN', 'COORDENADOR', 'AGENTE'] },
  { area: 'Associados', acao: 'Consultar associados', papeis: ['ADMIN', 'TESOUREIRO', 'COORDENADOR', 'ANALISTA', 'AGENTE'] },
  { area: 'Contratos', acao: 'Criar contrato / operação', papeis: ['ADMIN', 'COORDENADOR', 'AGENTE'] },
  { area: 'Contratos', acao: 'Mudar etapa do contrato', papeis: ['ADMIN', 'COORDENADOR', 'TESOUREIRO'] },
  { area: 'Esteira', acao: 'Abrir card (operação nova)', papeis: ['ADMIN', 'AGENTE'] },
  { area: 'Esteira', acao: 'Analisar / aprovar operação nova', papeis: ['ADMIN', 'ANALISTA'] },
  { area: 'Esteira', acao: 'Avalizar renovação', papeis: ['ADMIN', 'COORDENADOR'] },
  { area: 'Esteira', acao: 'Pagar / liberar (tesouraria)', papeis: ['ADMIN', 'TESOUREIRO'] },
  { area: 'Renovações', acao: 'Ver aptos a renovar', papeis: ['ADMIN', 'TESOUREIRO', 'COORDENADOR', 'ANALISTA', 'AGENTE'] },
  { area: 'Financeiro', acao: 'Ver resumo financeiro', papeis: ['ADMIN', 'TESOUREIRO', 'COORDENADOR'] },
  { area: 'Financeiro', acao: 'Lançar / excluir despesa', papeis: ['ADMIN', 'TESOUREIRO'] },
  { area: 'Financeiro', acao: 'Registrar pagamento', papeis: ['ADMIN', 'TESOUREIRO'] },
  { area: 'Devoluções', acao: 'Lançar devolução / ressarcir', papeis: ['ADMIN', 'TESOUREIRO', 'COORDENADOR'] },
  { area: 'Inadimplência', acao: 'Consultar inadimplência', papeis: ['ADMIN', 'TESOUREIRO', 'COORDENADOR', 'ANALISTA'] },
  { area: 'Importação', acao: 'Importar ETIPI / processar mês', papeis: ['ADMIN'] },
  { area: 'Relatórios', acao: 'Ver relatórios', papeis: ['ADMIN', 'TESOUREIRO', 'COORDENADOR', 'ANALISTA', 'AGENTE'] },
  { area: 'Usuários', acao: 'Gerenciar usuários e acessos', papeis: ['ADMIN'] },
  { area: 'Configurações', acao: 'Alterar configurações do sistema', papeis: ['ADMIN'] },
];

export function AcessosPage() {
  // agrupa ações por área
  const areas = [...new Set(ACOES.map((a) => a.area))];

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-primary" /> Acessos por Papel
        </h1>
        <p className="text-sm text-muted-foreground">O que cada nível de acesso pode ver e fazer no sistema</p>
      </div>

      {/* legenda dos papéis */}
      <div className="mb-4 grid grid-cols-1 md:grid-cols-5 gap-2">
        {PAPEIS.map((p) => (
          <Card key={p.code}><CardContent className="p-3">
            <p className="font-semibold text-sm">{p.label}</p>
            <p className="text-xs text-muted-foreground">{p.desc}</p>
          </CardContent></Card>
        ))}
      </div>

      <Card>
        <CardHeader><CardTitle>Matriz de permissões</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-gray-500">
                <th className="py-2 text-left pr-4">Ação</th>
                {PAPEIS.map((p) => (
                  <th key={p.code} className="py-2 px-2 text-center whitespace-nowrap">{p.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {areas.map((area) => (
                <Fragment key={area}>
                  <tr className="bg-muted/40">
                    <td colSpan={PAPEIS.length + 1} className="py-1.5 px-2 font-semibold text-xs uppercase text-muted-foreground">{area}</td>
                  </tr>
                  {ACOES.filter((a) => a.area === area).map((a, i) => (
                    <tr key={`${area}-${i}`} className="border-b">
                      <td className="py-2 pr-4">{a.acao}</td>
                      {PAPEIS.map((p) => (
                        <td key={p.code} className="py-2 px-2 text-center">
                          {a.papeis.includes(p.code)
                            ? <Check className="h-4 w-4 text-green-600 inline" />
                            : <Minus className="h-4 w-4 text-gray-300 dark:text-gray-600 inline" />}
                        </td>
                      ))}
                    </tr>
                  ))}
                </Fragment>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <p className="mt-4 text-xs text-muted-foreground">
        O Administrador (Master) tem acesso a todas as funções. Esta matriz reflete as permissões configuradas no sistema.
      </p>
    </div>
  );
}
